import { useState } from 'react';
import { Send, Mail, Calendar } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CALENDLY_URL = 'https://calendly.com/hello-kingmakersinc/discovery';

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const inputClasses =
  'w-full h-12 px-4 rounded-[10px] bg-km-gray-2 border border-km-gray-3 font-inter font-medium text-sm text-km-white placeholder:text-km-gray-5/70 focus:outline-none focus:ring-2 focus:ring-km-gold/60 focus:border-km-gold/40 transition-all duration-300';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: 'General Inquiry', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', subject: 'General Inquiry', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div data-testid="contact-page" className="px-6 lg:px-8 pt-[80px] pb-[100px] md:pt-[120px] md:pb-[120px]">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
          <div>
            <RevealSection>
              <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4">Contact</p>
              <h1 className="font-inter font-bold text-[38px] md:text-[54px] lg:text-[68px] leading-[1.1] tracking-tight text-km-white">
                Let's talk clarity.
              </h1>
              <p className="mt-6 font-inter font-medium text-base md:text-lg text-km-gray-5 leading-[1.3]">
                Have a question about our products? Interested in advisory services? Or just want to explore whether King Makers is the right fit for your situation?
              </p>
            </RevealSection>

            <RevealSection delay={100}>
              <div className="mt-12 space-y-[20px]">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block p-[30px] rounded-[10px] bg-km-card-gradient border-2 border-km-gold/30 shadow-km-gold-soft transition-all duration-300 hover:border-km-gold/60 cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-km-gold-gradient shadow-km-gold-glow">
                      <Calendar className="h-5 w-5 text-km-gray-1" />
                    </div>
                    <div>
                      <h3 className="font-inter font-medium text-[20px] leading-[1.2] text-km-white">Book a call</h3>
                      <p className="mt-1 font-inter font-medium text-sm text-km-gold group-hover:text-km-gold-deep transition-colors">
                        Schedule via Calendly
                      </p>
                    </div>
                  </div>
                </a>

                <div className="p-[30px] rounded-[10px] bg-km-card-gradient border border-km-gray-3">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-km-icon-gradient">
                      <Mail className="h-5 w-5 text-km-white" />
                    </div>
                    <div>
                      <h3 className="font-inter font-medium text-[20px] leading-[1.2] text-km-white">Email</h3>
                      <a
                        href="mailto:hello@kingmakersinc.ca"
                        className="mt-1 block font-inter font-medium text-sm text-km-gray-5 hover:text-km-gold transition-colors cursor-pointer"
                      >
                        hello@kingmakersinc.ca
                      </a>
                      <p className="mt-2 font-inter font-medium text-[13px] text-km-gray-5/80">
                        Typically within 24 hours on business days.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </RevealSection>
          </div>

          <RevealSection delay={150}>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="p-[30px] md:p-[40px] rounded-[10px] bg-km-card-gradient border border-km-gray-3 space-y-5"
            >
              <div>
                <label htmlFor="name" className="block font-inter text-sm font-medium text-km-white mb-2">Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  data-testid="contact-name"
                  required
                  className={inputClasses}
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block font-inter text-sm font-medium text-km-white mb-2">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  data-testid="contact-email"
                  required
                  className={inputClasses}
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block font-inter text-sm font-medium text-km-white mb-2">Subject</label>
                <select
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  data-testid="contact-subject"
                  className={`${inputClasses} cursor-pointer`}
                >
                  <option>General Inquiry</option>
                  <option>Product Question</option>
                  <option>Service Interest</option>
                  <option>Partnership</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block font-inter text-sm font-medium text-km-white mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  data-testid="contact-message"
                  required
                  rows={5}
                  className={`${inputClasses} h-auto py-3 resize-none`}
                  placeholder="Tell us about your situation..."
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={status === 'loading'}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-[10px] bg-km-gold text-km-gray-1 font-inter font-medium text-sm tracking-wide hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {status === 'loading' ? 'Sending...' : 'Send Message'}
              </button>

              {status === 'success' && (
                <p data-testid="contact-success" className="font-inter font-medium text-sm text-km-gold text-center">
                  Message sent successfully. We'll be in touch.
                </p>
              )}
              {status === 'error' && (
                <p data-testid="contact-error" className="font-inter font-medium text-sm text-red-400 text-center">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          </RevealSection>
        </div>
      </div>
    </div>
  );
}
